import { useState } from 'react'
import { Camera, Check, Loader2, Pencil, Plus, Trash2, Undo2 } from 'lucide-react'

import { useAuth } from '../../auth/authContext'
import { Field } from '../../components/Field'
import { useRoadbook } from '../../data/roadbookContext'
import { travelDayLabel } from '../../lib/calendar'
import { darfBearbeiten } from '../../lib/diary'
import { clock, isoDate, today } from '../../lib/format'
import { activeStages, emptyDiary } from '../../lib/trip'
import type { DiaryEntry, DiaryPost, Stage } from '../../lib/types'
import { germanDate } from '../print/metrics'
import { EtappenAusAufzeichnung } from './EtappenAusAufzeichnung'
import { PhotoThumb } from './PhotoThumb'
import { TrackPanel } from './TrackPanel'
import { TrackVerteilen } from './TrackVerteilen'
import { useWeatherNachlauf } from './weatherApi'
import { WeatherPanel } from './WeatherPanel'
import { weatherJob } from './weatherRun'

/** Mehr Bilder je Etappe passen weder auf die Seite noch ins Datenvolumen. */
const MAX_PHOTOS = 8

export function DiaryView() {
  const { trip, darfSchreiben } = useRoadbook()
  // Der Nachlauf hängt an der Ansicht und nicht an einer einzelnen Etappe: er
  // holt das Wetter aller Tage nach, für die eine Aufzeichnung vorliegt und
  // noch nichts dasteht - sobald Netz da ist. Siehe useWeatherNachlauf.
  const wetter = useWeatherNachlauf()

  const stages = activeStages(trip)

  // Ein Tagebuch ohne Tage. Das Verteilen einer Aufzeichnung wäre hier nicht
  // nur nutzlos, sondern eine Falle: die Datei liesse sich wählen, zerlegen
  // und jedem Stück eine Etappe zuweisen, die es nicht gibt - und am Ende
  // stünde "Nichts ausgewählt" auf einem Knopf, der nie angeht.
  //
  // Das Anlegen aus einer Aufzeichnung steht trotzdem da, und genau hier
  // gehört es hin: es ist der einzige Weg, der aus einem leeren Tagebuch
  // herausführt, ohne zuerst von Hand eine Etappe zu planen. Wer gefahren ist,
  // ohne vorher etwas einzutragen, kommt mit seiner Datei genau auf diese
  // Seite.
  if (stages.length === 0) {
    return (
      <section className="seite">
        <p className="leer">
          Noch keine Etappe geplant. Das Tagebuch hängt an den Tagen der Reise: sobald die erste
          Etappe steht, gibt es hier eine Seite dafür.
        </p>
        {darfSchreiben && <EtappenAusAufzeichnung />}
      </section>
    )
  }

  return (
    <section className="seite">
      {/*
        Ganz oben, über den Tagen: eine Datei über mehrere Tage betrifft alle
        Etappen und keine einzelne. Dieselbe Stelle wie der Sammelaufruf für
        die Strecken in der Etappenliste - was viele Etappen auf einmal
        anfasst, steht vor ihnen und nicht in einer von ihnen.
      */}
      {darfSchreiben && <TrackVerteilen />}
      {/*
        Daneben und nicht statt dessen: verteilen füllt bestehende Etappen,
        anlegen macht neue. Beides kommt auch zusammen vor - wer sechzehn Tage
        geplant hat und spontan zwei Tage länger unterwegs war, verteilt die
        Aufzeichnung auf die geplanten Tage und legt die beiden übrigen an.
      */}
      {darfSchreiben && <EtappenAusAufzeichnung />}

      {stages.map((stage) => (
        <DiaryPage
          key={stage.id}
          stage={stage}
          tag={travelDayLabel(stages, stage)}
          wetterLaeuft={wetter.laeuft}
          onWetterHolen={(s, e) => {
            const job = weatherJob(s, e)
            if (job) void wetter.holen(job)
          }}
        />
      ))}
    </section>
  )
}

function DiaryPage({
  stage,
  tag,
  wetterLaeuft,
  onWetterHolen,
}: {
  stage: Stage
  /** Der Reisetag dieser Etappe, zweistellig. Siehe lib/calendar.ts. */
  tag: string
  wetterLaeuft: boolean
  onWetterHolen: (stage: Stage, entry: DiaryEntry) => void
}) {
  const { diary, setDiary, photosByStage, addPhoto, darfSchreiben } = useRoadbook()
  const entry = diary[stage.id] ?? emptyDiary(stage.id)
  const photos = photosByStage[stage.id] ?? []

  const [laedt, setLaedt] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  async function nimmDateien(files: FileList | null) {
    if (!files || files.length === 0) return
    setLaedt(true)
    setFehler(null)
    try {
      for (const file of Array.from(files).slice(0, MAX_PHOTOS - photos.length)) {
        await addPhoto(stage.id, file)
      }
    } catch (err) {
      console.error('Foto konnte nicht übernommen werden', err)
      setFehler('Das Bild liess sich nicht verarbeiten.')
    } finally {
      setLaedt(false)
    }
  }

  return (
    <article className="tagebuchseite">
      <div className="tagebuchkopf">
        <span className="tagnummer">{tag}</span>
        <h3>
          {stage.from} → {stage.to}
        </h3>
        {darfSchreiben ? (
          <button
            type="button"
            className="gefahren-knopf kein-druck"
            aria-pressed={entry.done}
            onClick={() => {
              const done = !entry.done
              // Beim ersten Abhaken gleich das Datum setzen: der häufigste Fall
              // ist "heute gefahren", und getippt wird unterwegs ungern.
              setDiary(stage.id, done && !entry.date ? { done, date: today() } : { done })
            }}
          >
            <Check size={14} /> {entry.done ? 'gefahren' : 'als gefahren markieren'}
          </button>
        ) : (
          entry.done && <span className="stempel kein-druck">gefahren</span>
        )}
      </div>

      {darfSchreiben && (
        <div className="raster-4 kein-druck">
          <Field label="Datum">
            <input
              type="date"
              value={entry.date}
              onChange={(e) => setDiary(stage.id, { date: e.target.value })}
            />
          </Field>
          <Field label="Tacho km">
            <input
              type="number"
              inputMode="numeric"
              value={entry.odo}
              onChange={(e) => setDiary(stage.id, { odo: e.target.value })}
            />
          </Field>
          <Field label="Tatsächlich km">
            <input
              type="number"
              inputMode="numeric"
              value={entry.realKm}
              placeholder={String(stage.km)}
              onChange={(e) => setDiary(stage.id, { realKm: e.target.value })}
            />
          </Field>
          <Field label="Wetter">
            <input
              value={entry.weather}
              placeholder="sonnig, 31 °C"
              onChange={(e) => setDiary(stage.id, { weather: e.target.value })}
            />
          </Field>
        </div>
      )}

      {/*
        Dieselben Angaben, nur als Text. Im Druck immer, am Bildschirm dann,
        wenn es keine Felder gibt: die Tatsachen des Tages sollen auch der
        sehen, der sie nicht ändern darf.
      */}
      <div className={darfSchreiben ? 'nur-druck' : 'tagesfakten'}>
        {entry.date && <span>{entry.date}</span>}
        {entry.odo && <span>Tacho {entry.odo}</span>}
        {entry.weather && <span>{entry.weather}</span>}
      </div>

      <Beitraege stage={stage} />

      <TrackPanel stage={stage} entry={entry} nurLesen={!darfSchreiben} />

      {/*
        Unter der Aufzeichnung, weil er aus ihr kommt: ohne Track steht hier
        nichts, und wer wissen will, warum, sieht direkt darüber, dass keine
        Datei da ist.
      */}
      <WeatherPanel
        stage={stage}
        entry={entry}
        laeuft={wetterLaeuft}
        onHolen={onWetterHolen}
        nurLesen={!darfSchreiben}
      />

      <div className="bilder">
        {photos.map((photo) => (
          <PhotoThumb key={photo.id} photo={photo} stageTo={stage.to} />
        ))}

        {darfSchreiben && photos.length < MAX_PHOTOS && (
          <label className="bild-hinzu kein-druck">
            {laedt ? <Loader2 size={16} className="dreht" /> : <Camera size={16} />}
            <span>Foto</span>
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                void nimmDateien(e.target.files)
                e.target.value = ''
              }}
            />
          </label>
        )}
      </div>
      {fehler && <p className="hinweis kein-druck">{fehler}</p>}
    </article>
  )
}

/**
 * Die Beiträge eines Tages, untereinander, und darunter das Schreibfeld.
 *
 * Ältester zuerst und das Schreibfeld unten: der Abend wird von oben nach unten
 * gelesen, und wer schreibt, hängt hinten an. Ein Nachrichtenstrom mit dem
 * Neuesten oben wäre die andere Ordnung, aber ein Tagebuch liest man vorwärts.
 *
 * Wer schreibt, legt einen neuen Beitrag an, statt einen bestehenden zu
 * verlängern. Genau daran hängt der ganze Umbau: zwei Leute, die am selben
 * Abend schreiben, fassen so nie dasselbe Dokument an.
 *
 * Kein Kasten je Beitrag, keine Sprechblase, kein Bildchen neben dem Namen. Die
 * Liste sieht aus wie die anderen Listen dieser App: eine feine Linie zwischen
 * den Einträgen, der Name klein darüber, die Knöpfe rechts.
 */
function Beitraege({ stage }: { stage: Stage }) {
  const { postsByStage, addPost, darfSchreiben } = useRoadbook()
  const [entwurf, setEntwurf] = useState('')

  const beitraege = postsByStage[stage.id] ?? []

  function eintragen() {
    if (!entwurf.trim()) return
    addPost(stage.id, entwurf)
    setEntwurf('')
  }

  return (
    <section className="beitraege">
      {beitraege.length > 0 && (
        <ul className="beitragsliste">
          {beitraege.map((post) => (
            <Beitrag key={post.id} post={post} />
          ))}
        </ul>
      )}

      {/* Ohne Schreibrecht kein Schreibfeld: ein Feld, das zum Tippen einlädt
          und den Text danach verwirft, ist eine Falle. */}
      {darfSchreiben && (
        <>
          <label className="feld kein-druck">
            <span>{beitraege.length > 0 ? 'Noch etwas dazu?' : 'Was war heute los?'}</span>
            <textarea
              rows={4}
              placeholder="Wo geschlafen, wen getroffen, was ist am Fahrzeug aufgefallen …"
              value={entwurf}
              onChange={(e) => setEntwurf(e.target.value)}
            />
          </label>
          <div className="knopfreihe kein-druck">
            <button type="button" className="knopf" disabled={!entwurf.trim()} onClick={eintragen}>
              <Plus size={14} /> Eintragen
            </button>
          </div>
        </>
      )}
    </section>
  )
}

/** Ein einzelner Beitrag: Verfasser, Zeit, Text - und beim eigenen die Knöpfe. */
function Beitrag({ post }: { post: DiaryPost }) {
  const { updatePost, deletePost, darfSchreiben } = useRoadbook()
  const { userId, displayName } = useAuth()
  const [entwurf, setEntwurf] = useState<string | null>(null)

  // Zwei Bedingungen, zwei Fragen: gehört mir der Beitrag, und darf ich in
  // dieser Reise überhaupt schreiben.
  const eigener = darfSchreiben && darfBearbeiten(post, { id: userId, name: displayName })

  if (entwurf !== null) {
    return (
      <li className="beitrag">
        <label className="feld">
          <span>Beitrag ändern</span>
          <textarea rows={4} value={entwurf} onChange={(e) => setEntwurf(e.target.value)} />
        </label>
        <div className="beitrag-knoepfe">
          <button
            type="button"
            className="knopf"
            onClick={() => {
              updatePost(post.id, entwurf)
              setEntwurf(null)
            }}
          >
            <Check size={14} /> Speichern
          </button>
          <button type="button" className="knopf still" onClick={() => setEntwurf(null)}>
            <Undo2 size={14} /> Abbrechen
          </button>
        </div>
      </li>
    )
  }

  return (
    <li className={eigener ? 'beitrag beitrag--eigener' : 'beitrag'}>
      <div className="beitrag-kopf">
        <span className="beitrag-autor">{post.authorName || 'Unbekannt'}</span>
        {/*
          Die Zeit steht hier und nicht im Buch. Am Bildschirm hilft sie beim
          Wiederfinden - "das von gestern Abend" -, auf Papier wäre sie eine
          Angabe über die Bedienung der App und nicht über die Reise.
        */}
        <span className="beitrag-zeit">{geschrieben(post)}</span>
        {eigener && (
          <div className="beitrag-knoepfe kein-druck">
            <button type="button" className="knopf still" onClick={() => setEntwurf(post.text)}>
              <Pencil size={14} /> Ändern
            </button>
            <button
              type="button"
              className="knopf still"
              onClick={() => {
                if (window.confirm('Diesen Beitrag löschen?')) deletePost(post.id)
              }}
            >
              <Trash2 size={14} /> Löschen
            </button>
          </div>
        )}
      </div>
      <p className="beitrag-text">{post.text}</p>
    </li>
  )
}

/**
 * Wann der Beitrag geschrieben wurde, kurz.
 *
 * Ohne Zeitpunkt steht nichts da. Das ist der überführte Text aus der Zeit vor
 * den Beiträgen: wann er entstanden ist, hat nie jemand festgehalten, und ein
 * erfundenes Datum wäre schlechter als keines.
 */
function geschrieben(post: DiaryPost): string {
  if (!post.createdAt) return ''
  const datum = germanDate(isoDate(post.createdAt))
  const zeit = clock(post.createdAt)
  const geaendert = post.updatedAt > post.createdAt ? ' · geändert' : ''
  return `${datum}, ${zeit}${geaendert}`
}
